/**
 * <dr24-disclosure> — toggles a region with a single trigger button.
 *
 * Markup: <dr24-disclosure> containing a <button data-dr24-disclosure-trigger aria-controls="region-id" aria-expanded="false">.
 * The region lives anywhere in the document (by id). Inside the button, [data-label-open] / [data-label-closed]
 * spans are switched with the `hidden` attribute so exactly one label is visible per state.
 *
 * - Initialises on connect, so it also works after the editor re-renders the region (custom elements upgrade on insertion).
 * - The region is forced closed on connect, independent of server markup or cached CSS.
 * - Native <button> gives Enter/Space activation and focus; aria-expanded reflects state.
 */
class Dr24Disclosure extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const trigger = this.querySelector('[data-dr24-disclosure-trigger]');
    if (!trigger) return;
    this.#sync(trigger, false);
    trigger.addEventListener(
      'click',
      () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.#sync(trigger, !isOpen);
      },
      { signal: this.#ac.signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync(trigger, open) {
    trigger.setAttribute('aria-expanded', String(open));
    const region = document.getElementById(trigger.getAttribute('aria-controls') || '');
    if (region) region.hidden = !open;
    for (const label of trigger.querySelectorAll('[data-label-open]')) label.hidden = open;
    for (const label of trigger.querySelectorAll('[data-label-close]')) label.hidden = !open;
  }
}

if (!customElements.get('dr24-disclosure')) {
  customElements.define('dr24-disclosure', Dr24Disclosure);
}
